import { SdxAdminForbiddenError } from '@sodexo/admin-module-react';
import type { InfiniteData } from '@tanstack/query-core';
import {
  type QueryKey,
  type UndefinedInitialDataInfiniteOptions,
  type UseMutationOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
  type WithRequired,
} from '@tanstack/react-query';
import type { FetchError } from '../services/fetch.service';

export type PswInfiniteDataOptions<
  TQueryFnData = unknown,
  TData = InfiniteData<TQueryFnData>,
  FetchErrorData = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
> = Partial<
  UndefinedInitialDataInfiniteOptions<TQueryFnData, FetchError<FetchErrorData>, TData, TQueryKey, TPageParam>
>;

export type PswQueryOptions<
  TQueryFnData = unknown,
  TData = TQueryFnData,
  FetchErrorData = unknown,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<UseQueryOptions<TQueryFnData, FetchError<FetchErrorData>, TData, TQueryKey>, 'queryKey'>;

export type PswMutationOptions<
  TData = unknown,
  TVariables = void,
  FetchErrorData = unknown,
  TContext = unknown,
> = UseMutationOptions<TData, FetchError<FetchErrorData>, TVariables, TContext>;

export function usePswQuery<
  TQueryFnData = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  FetchErrorData = unknown,
>(options: PswQueryOptions<TQueryFnData, TData, FetchErrorData, TQueryKey> & { queryKey: TQueryKey }) {
  const queryClient = useQuery(options);

  return {
    ...queryClient,
    isForbiddenByAdminModule: (queryClient.error?.data as SdxAdminForbiddenError)?.name === SdxAdminForbiddenError.name,
  };
}

export function usePswInfiniteQuery<
  TQueryFnData = unknown,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
  FetchErrorData = unknown,
>(
  options: WithRequired<
    PswInfiniteDataOptions<TQueryFnData, TData, FetchErrorData, TQueryKey, TPageParam>,
    'getNextPageParam' | 'queryKey' | 'initialPageParam'
  >,
) {
  const queryClient = useInfiniteQuery(options);

  return {
    ...queryClient,
    isForbiddenByAdminModule: (queryClient.error?.data as SdxAdminForbiddenError)?.name === SdxAdminForbiddenError.name,
  };
}
